import { object, string } from 'yup';
import { useForm } from 'react-hook-form';
import { Button, Icon, InputInLabel } from '@/components/ui';
import { useSelector } from 'react-redux';
import { yupResolver } from '@hookform/resolvers/yup';
import { Link, useLocation, useParams } from 'react-router-dom';
import React, { useEffect, useRef, useState, useMemo } from 'react';
import {
	getFilterMiniPopupStatus,
	getFilterPopupStatus,
	setFilterMiniPopupStatus,
	setSavedFiltersIds,
} from '@/stores/slices/filterPopupSlice';
import {
	setAuthorizationStatus,
	setFilterMode,
} from '@/stores/slices/popupSlice';
import useLadyService from '@/services/LadyService';
import showToast from '../toast/Toast';
import { makeRequest } from '@/services/makeRequest';

import './notifications.scss';
import { useFilterLogicContext } from '../filter/FilterLogicProvider';

const SaveFilterForm = ({ fetchData }) => {
	const { dispatch, userType, navigate, t, lang, makeRequest } =
		useLadyService();

	const isLogin = userType !== 'default';

	const formSchema = object().shape({
		name: string().required(t('required')),
	});

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm({
		mode: 'onSubmit',
		resolver: yupResolver(formSchema),
	});

	const [saveText, setSaveText] = useState(false);

	const onSubmit = async (data) => {
		console.log(`=> saving filter: `, data);

		const result = await fetchData(data, 'post');

		console.log(`=> result: `, result);

		if (!!result) {
			setSaveText(true);
			reset();
			dispatch(setSavedFiltersIds(result));
		}
	};

	const lkLink = useMemo(() => {
		switch (userType) {
			case 'indi':
				return `/lk/profiles`;
			case 'photograph':
				return `/${lang}/lk/photograph`;
			case 'agency':
				return `/lk/agency`;
			case 'customer':
				return `/lk/customer/subscriptions`;
			default:
				return `/`;
		}
	}, [userType, lang]);

	const toLk = (e) => {
		if (userType === 'default') {
			dispatch(setFilterMode(true));
			dispatch(setAuthorizationStatus(true));
		} else {
		}
		e.preventDefault();
		navigate(`${lkLink}`);
	};

	const handleSave = () => {
		!isLogin && dispatch(setFilterMode(true));
		!isLogin && dispatch(setAuthorizationStatus(true));
	};

	return (
		<>
			<form
				onSubmit={handleSubmit(onSubmit)}
				className="input-label__wrapper notifications__safe-filter"
			>
				<fieldset>
					<legend className={errors.name ? 'input-label__error' : ''}>
						{errors.name ? errors.name.message : t('filtername')}
					</legend>
					<InputInLabel
						clazz={`w-100 ${errors.name ? ' _error' : ''}`}
						id={'loginRegistration'}
						type={'text'}
						placeholder={t('filterplaceholder')}
						register={{ ...register('name') }}
						autocomplete={'off'}
						onChange={() => {}}
					></InputInLabel>
				</fieldset>

				<Button
					buttonType={isLogin ? 'submit' : 'button'}
					clazz={'button-square'}
					onClick={handleSave}
				>
					{' '}
					{t('save')}
				</Button>
			</form>
			{saveText ? (
				<p className={'notifications__text mb-32'}>
					{t('filterissave')} {t('at')}{' '}
					<Link
						className={'green hover-line'}
						to={`/${lang}/lk/${userType}/subscriptions`}
					>
						{t('subandfilters').toLowerCase()}
					</Link>
				</p>
			) : (
				<p className={'notifications__text mb-32'}>
					{t('savedfiltersaviable')}{' '}
					<Link
						to={lkLink}
						onClick={(e) => toLk(e)}
					>
						{t('profile')}
					</Link>
				</p>
			)}
		</>
	);
};

const Notifications = () => {
	const {
		dispatch,
		t,
		windowWidth,
		city,
		userType,
		// emailVerifyAt,
		// userEmail,
		// userTg,
		// userWa,
	} = useLadyService();
	const statusFullPopup = useSelector(getFilterPopupStatus);
	const statusMiniPopup = useSelector(getFilterMiniPopupStatus);
	// const filterQuery = useSelector(getFilterQuery);

	const { uuid } = useParams();
	// const location = useLocation();

	const FILTER = useFilterLogicContext();

	const formSchema = object().shape({
		email: string().email().required(t('emailisnes')),
		text: string(),
	});

	const onSubmitBig = async (data) => {
		try {
			const result = await fetchData(data, 'post');

			if (!!result) {
				showToast({
					message: t('success'),
					variant: 'success',
				});
				reset();
			} else {
				showToast({
					message: t('oops'),
					variant: 'error',
				});
			}
		} catch (error) {
			console.error('error: ', error);
		}
	};

	const {
		register,
		handleSubmit,
		reset,
		getValues,
		formState: { errors },
	} = useForm({
		mode: 'onSubmit',
		resolver: yupResolver(formSchema),
	});

	const [email, setEmail] = useState();
	const recaptchaRef = useRef(null);

	const fetchData = async (data, type = 'post', id = false) => {
		try {
			const route = `user/filter${type === 'put' ? `/${id}` : ''}`;

			const method = type === 'post' ? 'POST' : 'PUT';

			const payload = {
				filter: FILTER.filterState.filter_query,
				filterState: { ...FILTER.filterState, city },
				title: data.name,
				email: data.email,
			};
			if (data.text?.length) {
				payload.message = data.text;
			}
			const result = await makeRequest({ route, method, payload });
			if (result && type === 'post') {
				return result.data.id;
			} else {
				return false;
			}
		} catch (error) {
			console.error('error: ', error);
			showToast({
				message: t('oops'),
				variant: 'error',
			});
			return false;
		}
	};

	const [messageError, setMessageError] = useState(false);
	const [emailError, setEmailError] = useState(false);

	const [isActive, setIsActive] = useState(false);

	useEffect(() => {
		if (
			statusFullPopup &&
			statusMiniPopup &&
			windowWidth &&
			FILTER.isOtherFilterActive
		) {
			localStorage.setItem('save_filter', 'shown');
			setIsActive(true);
		}
	}, [statusFullPopup]);

	const handleClick = () => {
		setIsActive(true);
	};

	// const [notifyType, setNotifyType] = useState([]);
	// const [isEmailConfirm, setIsEmailConfirm] = useState(false);
	// const [isWaConfirm, setIsWaConfirm] = useState(false);

	// const handleNotifyType = (value) => {
	// 	if (value === 'email' && !emailVerifyAt) {
	// 		setIsEmailConfirm(true);
	// 		return;
	// 	}
	// 	if (value === 'whatsapp') {
	// 		setIsWaConfirm(true);
	// 		return;
	// 	}
	// 	setNotifyType((prev) => {
	// 		if (notifyType.includes(value)) {
	// 			return prev.filter((item) => item !== value);
	// 		} else {
	// 			return [...prev, value];
	// 		}
	// 	});
	// };

	const isShowSaveFilter = useMemo(() => {
		return !FILTER.isFilterEmpty && FILTER.isOtherFilterActive && !uuid;
	}, [FILTER.isFilterEmpty, FILTER.isOtherFilterActive, uuid]);

	useEffect(() => {
		const saveFilterOpenAuto = localStorage.getItem('save_filter');

		if (!saveFilterOpenAuto === 'shown' && isShowSaveFilter) {
			setIsActive(true);
			localStorage.setItem('save_filter', 'shown');
		}
	}, [isShowSaveFilter]);

	return (
		<>
			{isShowSaveFilter && (
				<Button
					onClick={handleClick}
					clazz="button button-cornered notifications__main-button user__save"
				>
					<span>{t('savesub')}</span>
					<Icon
						size={'m'}
						spritePath={'save'}
					/>
				</Button>
			)}

			{isActive ? (
				<div
					className={`popup-form__body notifications${
						isActive ? ' _show' : ''
					}`}
				>
					<Button
						onClick={() => {
							setIsActive(false);
							dispatch(setFilterMiniPopupStatus(false));
						}}
						clazz={'button__close'}
					>
						<Icon
							size={'l'}
							spritePath={'close'}
						/>
					</Button>

					{userType !== 'agency' && userType !== 'indi' && (
						<>
							<h2 className=" popup-form__title mb-32 mt-0">
								{t('savefilters')}
							</h2>

							<SaveFilterForm
								onSubmitForm={setIsActive}
								fetchData={fetchData}
							/>
						</>
					)}

					<div className="followtelegram bg-none">
						{t('telegramfollow')}
						<br /> {t('telfol2')}
						<a
							rel="noreferrer"
							href="https://t.me/+AUd9TM1WaDE4Mzk8"
							target="_blank"
							className="button button--primary"
						>
							<i className="icon icon-l icon--telegram"></i>
							Lady4LoveCZ
						</a>
					</div>

					{/* <hr className={'mb-18 mt-12'} /> */}

					{/* <form
						className={'notifications__subscribe'}
						onSubmit={handleSubmit(onSubmitBig)}
					>
						<h2 className="popup-form__title mt-0 mb-32 text-center">
							{t('notifyaboutnew')}
						</h2>

						<fieldset className={'mb-20'}>
							<legend>{t('notificationmessage')}</legend>
							<label
								htmlFor="saveFilterText"
								className={`${
									messageError ? 'error' : ''
								} input-label p`}
							>
								<textarea
									{...register('text')}
									placeholder={t('wenotifyaboutnew')}
									id={'saveFilterText'}
									onChange={() => {
										if (messageError) {
											setMessageError(false);
										}
									}}
								/>
							</label>
						</fieldset>

						<fieldset className={'mb-20'}>
							<legend>
								{(errors.email && (
									<span className={'input-label__error'}>
										{errors.email.message}
									</span>
								)) || <span>{t('youremail')}</span>}
							</legend>
							<InputInLabel
								clazz={`${emailError ? ' error' : ''} mb-12`}
								id={'subscribeFilterEmail'}
								type={'email'}
								placeholder={'example@google.com'}
								register={{ ...register('email') }}
								autocomplete={'email'}
								value={!!userEmail ? userEmail : ''}
								onChange={() => {
									if (emailError) {
										setEmailError(false);
									}
								}}
							></InputInLabel>
						</fieldset>

						 <>
                <span>{t("notify") + ":"}</span>
                <div className="d-flex gap-8 align-center mt-12 mb-24">
                  <Checkbox
                    onChange={() => {
                      handleNotifyType("email");
                    }}
                    checked={notifyType.includes("email") || emailVerifyAt}
                    id={"notify-by-email"}
                    clazz={"width-max"}
                    title={"Email"}
                  ></Checkbox>
                  <Checkbox
                    onChange={() => {
                      handleNotifyType("telegram");
                    }}
                    checked={notifyType.includes("telegram")}
                    id={"notify-by-telegram"}
                    clazz={"width-max"}
                    title={"Telegram"}
                    disabled={!!userTg}
                  ></Checkbox>
                  <Checkbox
                    onChange={() => {
                      handleNotifyType("whatsapp");
                    }}
                    checked={notifyType.includes("whatsapp")}
                    id={"notify-by-whatsapp"}
                    clazz={"width-max"}
                    title={"WhatsApp"}
                    disabled={!!userWa}
                  ></Checkbox>
                </div>
                {isEmailConfirm && (
                  <div className="d-flex gap-8 align-center">
                    <span className="color-red-700">
                      {"Email - " + t("notconfirmed")}
                    </span>
                    <Link className="color-700" to={`/lk/${userType}#notify`}>
                      {t("confirm")}
                    </Link>
                  </div>
                )}
                {isWaConfirm && (
                  <div className="d-flex gap-8 align-center">
                    <span className="color-red-700">
                      {"WhatsApp - " + t("notconfirmed")}
                    </span>
                    <Link className="color-700" to={`/lk/${userType}#notify`}>
                      {t("confirm")}
                    </Link>
                  </div>
                )}
            </> 

						<Button
							size={'l'}
							buttonType={'submit'}
							clazz={'button--green'}
							onClick={() => {
								try {
									handleSubmit(onSubmitBig);
								} catch (error) {
									console.log('error: ', error);
								}
							}}
						>
							{t('subscribe')}
						</Button>
						{email ? (
							<p className={''}>
								{t('onthe')} {email} {t('linksent')}
							</p>
						) : null}
						<ReCAPTCHA
							ref={recaptchaRef}
							sitekey="6LfP8iUqAAAAAEPL4hfdJluwEE6cnuzXd_au2QuP"
							size="invisible"
						/>
					</form> */}
				</div>
			) : null}
		</>
	);
};
export default Notifications;
